var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useMemo, useRef } from 'react';
import { isBrowser, useValueControl } from '@snack-uikit/utils';
import { ITEM_PREFIXES } from '../../../constants';
import { HiddenTabButton } from '../../../helperComponents';
import { extractActiveItems, kindFlattenItems, useCreateBaseItems } from '../../Items';
import { CollapseContext, FocusListContext, NewListContextProvider, SelectionProvider } from '../contexts';
import { useNewKeyboardNavigation } from '../hooks';
import { ListPrivate } from '../ListPrivate';
import styles from '../styles.module.css';
export const List = forwardRef((_a, ref) => {
    var { items: itemsProp = [], search, pinBottom: pinBottomProp = [], pinTop: pinTopProp = [], footerActiveElementsRefs, onKeyDown, tabIndex = 0, className, collapse = {}, selection, contentRender, size = 's', marker = true, keyboardNavigationRef, hasListInFocusChain = true } = _a, props = __rest(_a, ["items", "search", "pinBottom", "pinTop", "footerActiveElementsRefs", "onKeyDown", "tabIndex", "className", "collapse", "selection", "contentRender", "size", "marker", "keyboardNavigationRef", "hasListInFocusChain"]);
    const hasSearch = useMemo(() => Boolean(search), [search]);
    const [openCollapseItems = [], setOpenCollapsedItems] = useValueControl(collapse);
    const toggleOpenCollapseItem = useCallback((id) => setOpenCollapsedItems((items) => (items === null || items === void 0 ? void 0 : items.includes(id)) ? items.filter(item => item !== id) : (items !== null && items !== void 0 ? items : []).concat([id])), [setOpenCollapsedItems]);
    const { searchItem, footerItems } = useCreateBaseItems({ footerActiveElementsRefs });
    /**
     * Объект с пропсами всех вложенных айтемов; ключ id
     */
    const _b = useMemo(() => {
        const pinTop = kindFlattenItems({
            items: pinTopProp,
            prefix: ITEM_PREFIXES.pinTop,
            parentId: ITEM_PREFIXES.default,
        });
        const items = kindFlattenItems({
            items: itemsProp,
            prefix: ITEM_PREFIXES.default,
            parentId: ITEM_PREFIXES.default,
        });
        const pinBottom = kindFlattenItems({
            items: pinBottomProp,
            prefix: ITEM_PREFIXES.pinBottom,
            parentId: ITEM_PREFIXES.default,
        });
        const flattenItems = Object.assign(Object.assign(Object.assign({}, pinTop.flattenItems), pinBottom.flattenItems), items.flattenItems);
        const focusFlattenItems = Object.assign(Object.assign(Object.assign({}, pinTop.focusFlattenItems), pinBottom.focusFlattenItems), items.focusFlattenItems);
        [...footerItems, searchItem].forEach(item => {
            flattenItems[item.id] = item;
            focusFlattenItems[item.id] = Object.assign(Object.assign({}, item), { originalId: item.id, items: [], key: item.id, allChildIds: [] });
        });
        return { items, pinTop, pinBottom, flattenItems, focusFlattenItems };
    }, [itemsProp, pinTopProp, pinBottomProp, searchItem, footerItems]), { flattenItems, focusFlattenItems } = _b, memorizedItems = __rest(_b, ["flattenItems", "focusFlattenItems"]);
    const { ids, expandedIds } = useMemo(() => {
        const { pinTop, items, pinBottom } = memorizedItems;
        let ids = [];
        let expandedIds = [];
        if (hasSearch) {
            ids.push(searchItem.id);
        }
        [pinTop, items, pinBottom].forEach(({ focusFlattenItems, focusCloseChildIds }) => {
            const activeItems = extractActiveItems({
                focusFlattenItems,
                focusCloseChildIds,
                openCollapseItems,
                isSelectionMultiple: (selection === null || selection === void 0 ? void 0 : selection.mode) === 'multiple',
            });
            ids = ids.concat(activeItems.ids);
            expandedIds = expandedIds.concat(activeItems.expandedIds);
        });
        footerItems.forEach(footerItem => {
            ids.push(footerItem.id);
        });
        return {
            ids,
            expandedIds,
        };
    }, [footerItems, hasSearch, memorizedItems, openCollapseItems, searchItem.id, selection === null || selection === void 0 ? void 0 : selection.mode]);
    const listRef = useRef(null);
    const btnRef = useRef(null);
    const firstItemId = ids[0];
    const { handleListKeyDownFactory, activeItemId, resetActiveItemId, forceUpdateActiveItemId } = useNewKeyboardNavigation({
        mainRef: listRef,
        btnRef,
        focusFlattenItems,
        keyboardNavigationRef,
        hasListInFocusChain,
        firstItemId,
    });
    const handleListKeyDown = useCallback((e) => handleListKeyDownFactory(ids, expandedIds)(e), [handleListKeyDownFactory, ids, expandedIds]);
    const isActive = isBrowser() && listRef.current === document.activeElement && activeItemId === undefined;
    const mergedHandlerKeyDown = (e) => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        handleListKeyDown === null || handleListKeyDown === void 0 ? void 0 : handleListKeyDown(e);
    };
    const handleOnFocus = () => {
        resetActiveItemId();
    };
    return (_jsx(NewListContextProvider, { flattenItems: flattenItems, focusFlattenItems: focusFlattenItems, contentRender: contentRender, size: size, marker: marker, firstItemId: firstItemId, virtualized: props.virtualized, children: _jsx(SelectionProvider, Object.assign({}, selection, { children: _jsx(CollapseContext.Provider, { value: {
                    openCollapseItems,
                    toggleOpenCollapseItem,
                }, children: _jsx(FocusListContext.Provider, { value: {
                        activeItemId,
                        handleListKeyDownFactory,
                        forceUpdateActiveItemId,
                    }, children: _jsxs("div", { className: cn(styles.wrapper, className), "data-active": isActive || undefined, children: [_jsx(ListPrivate, Object.assign({}, props, { items: memorizedItems.items.focusCloseChildIds, pinTop: memorizedItems.pinTop.focusCloseChildIds, pinBottom: memorizedItems.pinBottom.focusCloseChildIds, searchItem: searchItem, ref: mergeRefs(ref, listRef), onFocus: handleOnFocus, onKeyDown: mergedHandlerKeyDown, tabIndex: hasListInFocusChain ? tabIndex : undefined, search: search, nested: false })), hasListInFocusChain && _jsx(HiddenTabButton, { ref: btnRef, listRef: listRef, tabIndex: tabIndex })] }) }) }) })) }));
});
